<template>
    <Card :header="$t('overview')" overlay :loading="loading">
        <template #nav>
            <div :key="spaceId" class="nav-wrap gap-3">
                <Multiselect
                    :key="timeRangeString"
                    :fetch-options-function="fetchSources"
                    :placeholder="$t('sources')"
                    :selected-options-label="$t('selectedSources')"
                    preselect-all
                    searchable
                    track-by="id"
                    label="name"
                    select-label=""
                    :disabled="loading"
                    show-checkboxes
                    show-selected
                    multiple
                    :close-on-select="false"
                    :clear-on-select="false"
                    class="width-240"
                    @changeTrackBy="sources = $event"
                    @loading="sourcesLoading = $event"
                />
            </div>
        </template>

        <div class="funnel-wrap gap-2">
            <div class="funnel-box funnel-box-1">
                <div class="funnel-content">
                    <div v-if="requestImpressions.getLoading() || filtersLoading" class="mt-3 text-center">
                        <b-spinner small />
                    </div>
                    <template v-else>
                        <div class="title d-flex align-items-center font-size-20">
                            {{ $format.number(impressions.impressions, 0) }}
                            <Tooltip :text="$t('producerReportsSales.impressionsStatsTooltip')" class="ml-1" />
                        </div>
                        <div class="caption">&nbsp;</div>
                        <div class="caption">
                            {{ $t('impressionsWidget') }}
                            <template v-if="hasPermissions && impressionsAll.impressions">
                                ({{ $t('percent_of_total', { percent: $format.percent((impressions.impressions / impressionsAll.impressions) * 100) }) }})
                            </template>
                        </div>
                    </template>
                </div>
                <div class="funnel-bg">
                    <svg preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" width="340" height="200">
                        <path fill="#91cc75" d="M0,0 L315,20 L340,100 L315,180 L0,200 Z" />
                    </svg>
                </div>
            </div>

            <div class="funnel-box funnel-box-2">
                <div class="funnel-content">
                    <div v-if="requestClicks.getLoading() || filtersLoading" class="mt-3 text-center">
                        <b-spinner small />
                    </div>
                    <template v-else>
                        <div class="title d-flex align-items-center font-size-20">
                            {{ $format.number(clicks.clicks, 0) }}
                            <Tooltip :text="$t('dashboard.clicksTooltip')" class="ml-1" />
                        </div>
                        <div class="caption">CTR {{ $format.percent(ctr) }}</div>
                        <div class="caption">
                            {{ $t('productsSummary.clicks') }}
                            <template v-if="hasPermissions && clicksAll.clicks">
                                ({{ $t('percent_of_total', { percent: $format.percent((clicks.clicks / clicksAll.clicks) * 100) }) }})
                            </template>
                        </div>
                    </template>
                </div>
                <div class="funnel-bg">
                    <svg preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" width="272" height="160">
                        <path fill="#91cc75" d="M0,0 L247,16 L272,80 L247,144 L0,160 L21,80 Z" />
                    </svg>
                </div>
            </div>

            <div class="funnel-box funnel-box-3">
                <div class="funnel-content">
                    <div v-if="requestOrders.getLoading() || filtersLoading" class="mt-3 text-center">
                        <b-spinner small />
                    </div>
                    <template v-else>
                        <div class="title d-flex align-items-center font-size-20">
                            {{ $format.number(orders.orders, 0) }}
                            <Tooltip :text="$t('producerReportsSales.ordersStatsTooltip')" class="ml-1" />
                        </div>
                        <div class="caption">CR {{ $format.percent(cr) }}</div>
                        <div class="caption">
                            {{ hasPermissions ? $t('transactions.title') : $t('transactions.list') }}
                            <template v-if="hasPermissions && ordersAll.orders">
                                ({{ $t('percent_of_total', { percent: $format.percent((orders.orders / ordersAll.orders) * 100) }) }})
                            </template>
                        </div>
                    </template>
                </div>
                <div class="funnel-bg">
                    <svg preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" width="217" height="128">
                        <path fill="#91cc75" d="M0,0 L192,14 L217,64 L192,114 L0,128 L21,64 Z" />
                    </svg>
                </div>
            </div>
        </div>

        <div class="kpi-row gap-3">
            <WidgetStatChart
                class="kpi-shadow"
                :title="$t('producerDashboard.quantityOwnProducts')"
                :loading="requestSales.getLoading() || filtersLoading"
                :error="requestSales.getError()"
                :value="$format.number(sales.quantity, 0)"
                :difference="$format.percent(percentDiff(sales.quantity, sales.quantity_compare))"
            />
            <WidgetStatChart
                class="kpi-shadow"
                :title="$t('producerDashboard.quantityOtherProducts')"
                :loading="requestSales.getLoading() || filtersLoading"
                :error="requestSales.getError()"
                :value="$format.number(sales.quantity_other, 0)"
                :difference="$format.percent(percentDiff(sales.quantity_other, sales.quantity_other_compare))"
            />
            <WidgetStatChart
                class="kpi-shadow"
                :title="$t('producerDashboard.quantityUnknownProducts')"
                :loading="requestSales.getLoading() || filtersLoading"
                :error="requestSales.getError()"
                :value="$format.number(sales.quantity_unknown, 0)"
                :difference="$format.percent(percentDiff(sales.quantity_unknown, sales.quantity_unknown_compare))"
            />
        </div>
        <div class="kpi-row gap-3">
            <WidgetStatChart
                class="kpi-shadow"
                :title="$t('producerDashboard.salesAmountOwn')"
                :title-tooltip="$t('dashboard.salesAmountOwnTooltip')"
                :loading="requestSales.getLoading() || filtersLoading"
                :error="requestSales.getError()"
                :value="$format.currency(sales.amount)"
                :difference="$format.percent(percentDiff(sales.amount, sales.amount_compare))"
                tooltip-formatter="currency"
            />
            <WidgetStatChart
                class="kpi-shadow"
                :title="$t('producerDashboard.salesAmountOtherProducts')"
                :loading="requestSales.getLoading() || filtersLoading"
                :error="requestSales.getError()"
                :value="$format.currency(sales.amount_other)"
                :difference="$format.percent(percentDiff(sales.amount_other, sales.amount_other_compare))"
                tooltip-formatter="currency"
            />
            <WidgetStatChart
                class="kpi-shadow"
                :title="$t('producerDashboard.salesAmountUnknownProducts')"
                :loading="requestSales.getLoading() || filtersLoading"
                :error="requestSales.getError()"
                :value="$format.currency(sales.amount_unknown)"
                :difference="$format.percent(percentDiff(sales.amount_unknown, sales.amount_unknown_compare))"
                tooltip-formatter="currency"
            />
        </div>
    </Card>
</template>

<script>
    import Card from '@/components/common/Card.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import Tooltip from '@/components/common/Tooltip.vue';
    import WidgetStatChart from '@/views/common/dashboard/parts/WidgetStatChart.vue';
    import { calculateChangePercentage } from '@/utils/number';
    import salesReportMixin from '../salesReportMixin';
    import { fetchSources } from '../utils';

    export default {
        name: 'SalesOverviewFunnel',
        mixins: [salesReportMixin],
        components: { Card, Multiselect, Tooltip, WidgetStatChart },
        props: {
            hasPermissions: {
                type: Boolean,
                default: true,
            },
        },
        data() {
            return {
                requestImpressions: this.$req('userStats/ImpressionsStats'),
                requestClicks: this.$req('userStats/ClicksStats'),
                requestOrders: this.$req('userStats/OrdersStats'),
                requestSales: this.$req('userStats/SalesStats'),
                requestSources: this.$req('reportsSales/UniqueSources'),
                requestImpressionsAll: this.$req('userStats/ImpressionsStats'),
                requestClicksAll: this.$req('userStats/ClicksStats'),
                requestOrdersAll: this.$req('userStats/OrdersStats'),
                sources: [],
                sourcesLoading: false,
            };
        },
        computed: {
            filtersLoading() {
                return this.sourcesLoading;
            },
            loading() {
                return this.filtersLoading || this.requestImpressions.getLoading() || this.requestClicks.getLoading() || this.requestOrders.getLoading() || this.requestSales.getLoading();
            },
            apiParams() {
                const urlParams = { spaceId: this.spaceId };
                const { startDate, endDate, compareStartDate, compareEndDate } = this.timeRange || {};
                const requestParams = {
                    startDate,
                    endDate,
                    compareStartDate,
                    compareEndDate,
                    ...(this.sources?.length ? { sources: this.sources.join(',') } : {}),
                };
                if (this.parentComponent?.showProductGroupsResults && this.parentComponent?.groups?.length) {
                    requestParams.groups = this.parentComponent.groups.join(',');
                }
                return { urlParams, requestParams };
            },
            impressions() {
                return this.requestImpressions.getData() || {};
            },
            clicks() {
                return this.requestClicks.getData() || {};
            },
            orders() {
                return this.requestOrders.getData() || {};
            },
            sales() {
                return this.requestSales.getData() || {};
            },
            impressionsAll() {
                return this.requestImpressionsAll.getData() || {};
            },
            clicksAll() {
                return this.requestClicksAll.getData() || {};
            },
            ordersAll() {
                return this.requestOrdersAll.getData() || {};
            },
            ctr() {
                const clickCount = this.clicks.clicks || 0;
                const impressionCount = this.impressions.impressions || 0;
                return impressionCount ? (clickCount / impressionCount) * 100 : 0;
            },
            cr() {
                const orderCount = this.orders.orders || 0;
                const clickCount = this.clicks.clicks || 0;
                return clickCount ? (orderCount / clickCount) * 100 : 0;
            },
        },
        watch: {
            spaceId() {
                this.reload();
            },
            apiParams: {
                deep: true,
                handler(newValue, oldValue) {
                    if (JSON.stringify(newValue || {}) !== JSON.stringify(oldValue || {})) this.fetchAll();
                },
            },
        },
        methods: {
            percentDiff(newValue, oldValue) {
                return calculateChangePercentage(newValue, oldValue);
            },
            resetFilters() {
                this.reload();
            },
            reload() {
                this.resetData();
                this.fetchAll();
            },
            resetData() {
                this.sources = [];
                [this.requestImpressions, this.requestClicks, this.requestOrders, this.requestSales, this.requestImpressionsAll, this.requestClicksAll, this.requestOrdersAll].forEach(request => {
                    request?.abortAll?.();
                    request?.resetAll?.();
                });
            },
            fetchAll() {
                if (!this.spaceId || !this.timeRange) return;
                const params = this.apiParams;
                this.requestImpressions.get(params);
                this.requestClicks.get(params);
                this.requestOrders.get(params);
                this.requestSales.get(params);
                if (this.hasPermissions) {
                    const allParams = { urlParams: params.urlParams, requestParams: { ...params.requestParams } };
                    delete allParams.requestParams.sources;
                    this.requestImpressionsAll.get(allParams);
                    this.requestClicksAll.get(allParams);
                    this.requestOrdersAll.get(allParams);
                }
            },
            async fetchSources({ search }) {
                return fetchSources({ requestSources: this.requestSources, spaceId: this.spaceId, timeRange: this.timeRange, search });
            },
        },
    };
</script>

<style scoped lang="scss">
    .nav-wrap {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex-wrap: wrap;
    }
    .funnel-wrap {
        width: 100%;
        height: 220px;
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0 auto;
        text-align: center;
    }
    .funnel-box {
        position: relative;
        height: 100%;
        display: flex;
        align-items: center;
        .funnel-content {
            width: 100%;
            height: 100%;
            position: absolute;
            inset: 0;
            z-index: 2;
            display: flex;
            justify-content: center;
            align-items: center;
            flex-direction: column;
            .title {
                margin-bottom: 5px;
            }
            .caption {
                white-space: nowrap;
            }
        }
        .funnel-bg {
            width: 100%;
            height: 100%;
            position: absolute;
            inset: 0;
            z-index: 1;
            display: flex;
            align-items: center;
        }
        &.funnel-box-1 {
            width: 340px;
        }
        &.funnel-box-2 {
            width: 272px;
        }
        &.funnel-box-3 {
            width: 217px;
        }
    }
    .kpi-row {
        display: flex;
        justify-content: center;
        align-items: stretch;
        flex-wrap: wrap;
        margin-top: 32px;
        margin-bottom: 12px;
    }
    .kpi-row ::v-deep .card {
        max-width: 300px;
        min-width: 260px;
    }
    .kpi-shadow {
        box-shadow: 0 2px 8px rgba($black, 0.12);
    }
</style>
